import './instrument'; // KEEP ME FIRST!
import { createRoot, hydrateRoot } from 'react-dom/client';
import { DEFAULT_RESOURCES, initializeI18n } from '@tecnobit-srl/i18n';
import * as Sentry from '@sentry/react';
import { merge } from 'lodash-es';
import { VARIANT_RESOURCES } from '@variant';
import { App } from './components/app';

declare global {
  interface Window {
    fromSsr: true | undefined;
  }
}

hydrate().catch((e: unknown) => {
  // eslint-disable-next-line no-console -- No alternatives here
  console.error(e);
});

async function hydrate(): Promise<void> {
  const root = document.getElementById('app');
  if (!root) {
    throw new Error('No root element available');
  }

  const i18nInstance = window.fromSsr
    ? await initializeI18n(
        merge(DEFAULT_RESOURCES, VARIANT_RESOURCES),
        window.inertiaPage.locale,
        window.inertiaPage.fallbackLocale
      )
    : undefined;

  // Shared error handlers for both the create-root and hydrate paths. On the
  // hydrate path onRecoverableError is what surfaces hydration mismatches
  // (React #418/#425) together with the offending node's component stack —
  // without it those errors are swallowed as a minified console.error with no
  // way to tell which element diverged between SSR and client.
  const rootOptions = {
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      // eslint-disable-next-line no-console -- ignore
      console.warn('Uncaught error', error, errorInfo.componentStack);
    }),
    onCaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      // eslint-disable-next-line no-console -- ignore
      console.warn('Error', error, errorInfo.componentStack);
    }),
    onRecoverableError: Sentry.reactErrorHandler((error, errorInfo) => {
      // eslint-disable-next-line no-console -- ignore
      console.warn('Recoverable error', error, errorInfo.componentStack);
    }),
  };

  if (!window.fromSsr) {
    root.innerHTML = '';
    const reactRoot = createRoot(root, rootOptions);
    reactRoot.render(<App i18nInstance={i18nInstance} />);
  } else {
    hydrateRoot(root, <App i18nInstance={i18nInstance} />, rootOptions);
  }
}
